import React from "react";
import { Chip, Stack, Tooltip } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { formatearImporte, emptyMarcaKpi } from "./pygAnaliticoBuild";
import { calcDetalleMarcaEconomico } from "./marcaInteresUtils";
import { useTranslation } from "react-i18next";
import {
  pygPriorityLabel,
  translateMarcaReason,
} from "../../constants/pygLineI18n";

const CHIP_W = 168;

const chipBase = {
  height: 24,
  fontWeight: 600,
  fontSize: "0.75rem",
  width: CHIP_W,
  minWidth: CHIP_W,
  maxWidth: CHIP_W,
  justifyContent: "center",
  "& .MuiChip-label": {
    px: 0.75,
    width: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    display: "block",
    textAlign: "center",
  },
};

const ChipWithTooltip = ({ label, sx }) => (
  <Tooltip title={label} enterDelay={400}>
    <Chip size="small" label={label} sx={sx} />
  </Tooltip>
);

const MarcaKpiChips = ({ kpi, loading, prioridadMeta }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const data = { ...emptyMarcaKpi(), ...kpi };
  const economico = calcDetalleMarcaEconomico(data);

  const chipsWrapSx = {
    display: "grid",
    gridTemplateColumns: {
      xs: `repeat(2, ${CHIP_W}px)`,
      sm: `repeat(3, ${CHIP_W}px)`,
      lg: `repeat(6, ${CHIP_W}px)`,
    },
    gap: 0.75,
    width: "100%",
    maxWidth: "100%",
  };

  if (loading && !kpi) {
    return (
      <Stack sx={chipsWrapSx}>
        {Array.from({ length: 6 }).map((_, i) => (
          <Chip key={i} size="small" label="…" variant="outlined" sx={{ ...chipBase }} />
        ))}
      </Stack>
    );
  }

  if (!kpi && !loading) return null;

  const prioridadSx =
    prioridadMeta?.prioridad === "alta"
      ? {
          bgcolor: alpha(theme.palette.success.main, 0.2),
          borderColor: alpha(theme.palette.success.main, 0.5),
        }
      : prioridadMeta?.prioridad === "media"
        ? {
            bgcolor: alpha(theme.palette.warning.main, 0.18),
            borderColor: alpha(theme.palette.warning.main, 0.5),
          }
        : prioridadMeta?.prioridad === "baja"
          ? {
              bgcolor: alpha(theme.palette.grey[500], 0.12),
              borderColor: alpha(theme.palette.grey[500], 0.35),
            }
          : null;

  const prioridadLabel = pygPriorityLabel(
    t,
    prioridadMeta?.prioridad,
    prioridadMeta?.prioridadLabel,
  );
  const razonesLabel = (prioridadMeta?.razones || [])
    .map((r) => translateMarcaReason(t, r))
    .join(" · ");

  return (
    <Stack sx={chipsWrapSx}>
      <Tooltip title={razonesLabel || prioridadLabel} enterDelay={400}>
        <Chip
          size="small"
          label={prioridadLabel}
          sx={{
            ...chipBase,
            fontWeight: 800,
            border: "1px solid",
            ...prioridadSx,
          }}
        />
      </Tooltip>
      <ChipWithTooltip
        label={`${t("pygAnalitico.ventaFact")}: ${formatearImporte(data.venta)}`}
        sx={{
          ...chipBase,
          bgcolor: alpha(theme.palette.success.main, 0.12),
          color: "success.dark",
          border: "1px solid",
          borderColor: alpha(theme.palette.success.main, 0.35),
        }}
      />
      <ChipWithTooltip
        label={`${t("pygAnalitico.compraFact")}: ${formatearImporte(data.coste)}`}
        sx={{
          ...chipBase,
          bgcolor: alpha(theme.palette.error.main, 0.08),
          color: "error.dark",
          border: "1px solid",
          borderColor: alpha(theme.palette.error.main, 0.3),
        }}
      />
      <ChipWithTooltip
        label={`${t("pygAnalitico.pedVenta")}: ${formatearImporte(data.pedidosVenta)}`}
        sx={{
          ...chipBase,
          bgcolor: alpha(theme.palette.info.main, 0.12),
          color: "info.dark",
          border: "1px solid",
          borderColor: alpha(theme.palette.info.main, 0.4),
        }}
      />
      <ChipWithTooltip
        label={`${t("pygAnalitico.pedCompra")}: ${formatearImporte(data.pedidosCompra)}`}
        sx={{
          ...chipBase,
          bgcolor: alpha(theme.palette.warning.main, 0.14),
          color: "warning.dark",
          border: "1px solid",
          borderColor: alpha(theme.palette.warning.main, 0.4),
        }}
      />
      <ChipWithTooltip
        label={`${t("pygAnalitico.result")}: ${formatearImporte(economico.resultado)}`}
        sx={{
          ...chipBase,
          fontWeight: 700,
          bgcolor: alpha(
            economico.resultado >= 0
              ? theme.palette.success.main
              : theme.palette.error.main,
            0.14,
          ),
          color: economico.resultado >= 0 ? "success.dark" : "error.main",
          border: "1px solid",
          borderColor: alpha(
            economico.resultado >= 0
              ? theme.palette.success.main
              : theme.palette.error.main,
            0.45,
          ),
        }}
      />
    </Stack>
  );
};

export default MarcaKpiChips;
